import { z } from 'zod';
import type { ContractError } from './errors';
import { toContractError } from './errors';
import type { Result } from './result';
import { UuidSchema } from './uuid';

const IsoDateTimeSchema = z.string().datetime({ offset: true });

const AttemptResultSchema = z.object({
  kind: z.string(),
  score: z.number().int().nullish(),
  duration_ms: z.number().int().nullish(),
});

const AttemptCompositeSchema = z.object({
  id: UuidSchema,
  attempt_id: UuidSchema,
  template_version: z.string(),
  status: z.string(),
  output_object_key: z.string().nullish(),
  thumbnail_object_key: z.string().nullish(),
  render_meta_object_key: z.string().nullish(),
  error: z.string().nullish(),
  started_at: IsoDateTimeSchema.nullish(),
  completed_at: IsoDateTimeSchema.nullish(),
  duration_ms: z.number().int().nullish(),
  attempt_count: z.number().int(),
  trace_id: UuidSchema,
  composite_url: z.string().url().nullish(),
  thumbnail_url: z.string().url().nullish(),
});

const SessionAttemptSchema = z.object({
  id: UuidSchema,
  attempt_no: z.number().int(),
  state: z.string(),
  started_at: IsoDateTimeSchema.nullish(),
  ended_at: IsoDateTimeSchema.nullish(),
  result: AttemptResultSchema.nullish(),
  composites: z.array(AttemptCompositeSchema).nullish(),
});

const SessionResultSchema = z.object({
  kind: z.string(),
  score: z.number().int().nullish(),
  duration_ms: z.number().int().nullish(),
});

const RecordingJobSchema = z.object({
  id: UuidSchema,
  status: z.string(),
  camera_type: z.string(),
  media_role: z.string(),
  started_at: IsoDateTimeSchema.nullish(),
  ended_at: IsoDateTimeSchema.nullish(),
  track_name: z.string().nullish(),
  camera_key: z.string().nullish(),
  egress_id: z.string().nullish(),
  object_key_prefix: z.string().nullish(),
  bucket: z.string().nullish(),
});

const SessionMediaSchema = z.object({
  id: UuidSchema,
  camera_type: z.string(),
  camera_key: z.string(),
  media_role: z.string(),
  source: z.string(),
  source_ref: z.string().nullish(),
  storage_provider: z.string(),
  bucket: z.string(),
  object_key: z.string(),
  content_type: z.string().nullish(),
  duration_ms: z.number().int().nullish(),
  status: z.string().nullish(),
  media_url: z.string().url().nullish(),
});

const SessionEventSchema = z.object({
  id: z.number().int(),
  session_id: UuidSchema.nullish(),
  attempt_id: UuidSchema.nullish(),
  event_type: z.string(),
  ts: IsoDateTimeSchema,
  source: z.string(),
  payload: z.record(z.string(), z.unknown()),
});

const SessionRecordSchema = z.object({
  id: UuidSchema,
  user_id: UuidSchema.nullish(),
  user_email: z.string().nullish(),
  user_label: z.string().nullish(),
  clerk_external_user_id: z.string().nullish(),
  robot_id: UuidSchema,
  robot_name: z.string().nullish(),
  game_type_key: z.string().nullish(),
  state: z.string(),
  end_reason: z.string().nullish(),
  created_at: IsoDateTimeSchema.nullish(),
  started_at: IsoDateTimeSchema.nullish(),
  ended_at: IsoDateTimeSchema.nullish(),
  attempt_count: z.number().int().nullish(),
  attempts: z.array(SessionAttemptSchema).nullish(),
  result: SessionResultSchema.nullish(),
  ticket_state: z.string().nullish(),
  queue_position: z.number().int().nullish(),
  recording_status: z.string().nullish(),
});

const SessionDetailSchema = SessionRecordSchema.extend({
  recording_jobs: z.array(RecordingJobSchema).nullish(),
  media: z.array(SessionMediaSchema).nullish(),
  session_events: z.array(SessionEventSchema).nullish(),
});

const SessionListResponseSchema = z.union([
  z.array(SessionRecordSchema),
  z.object({ sessions: z.array(SessionRecordSchema) }),
]);

export type AttemptResult = z.infer<typeof AttemptResultSchema>;
export type AttemptComposite = z.infer<typeof AttemptCompositeSchema>;
export type SessionAttempt = z.infer<typeof SessionAttemptSchema>;
export type SessionResult = z.infer<typeof SessionResultSchema>;
export type RecordingJob = z.infer<typeof RecordingJobSchema>;
export type SessionMedia = z.infer<typeof SessionMediaSchema>;
export type SessionEvent = z.infer<typeof SessionEventSchema>;
export type SessionRecord = z.infer<typeof SessionRecordSchema>;
export type SessionDetail = z.infer<typeof SessionDetailSchema>;

const parseContract = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  raw: unknown,
): Result<z.infer<TSchema>, ContractError> => {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: 'http' }),
    };
  }
  return { ok: true, value: parsed.data };
};

export const parseSessionListResponse = (raw: unknown): Result<SessionRecord[], ContractError> => {
  const parsed = parseContract(SessionListResponseSchema, raw);
  if (!parsed.ok) {
    return parsed;
  }
  if (Array.isArray(parsed.value)) {
    return { ok: true, value: parsed.value };
  }
  return { ok: true, value: parsed.value.sessions };
};

export const parseSessionDetail = (raw: unknown) => parseContract(SessionDetailSchema, raw);

export const parseAttemptComposite = (raw: unknown) => parseContract(AttemptCompositeSchema, raw);
